import {
  useClientPayerFormDataQuery,
  usePayersQuery,
  useUpdateClientPayerMutation,
} from '@cartwheel/api/v1'
import { Button, LoadingView } from '@cartwheel/components'
import { useState } from 'react'
import PayerCombobox from './payer-combobox'

interface Props {
  clientId: string
  onComplete: () => void
}

export default function InsuranceEditForm({ clientId, onComplete }: Props) {
  const [dropdownSelections, setDropdownSelections] = useState<Record<string, string | null>>({})
  const [saveError, setSaveError] = useState<string | null>(null)

  const {
    data: formData,
    isLoading: isLoadingForm,
    error: formError,
  } = useClientPayerFormDataQuery({ path: { client_id: clientId } })

  const { data: payersData, isLoading: isLoadingPayers, error: payersError } = usePayersQuery()

  const updateMutation = useUpdateClientPayerMutation()

  const isLoading = isLoadingForm || isLoadingPayers
  const error = formError || payersError

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingView title="Loading insurance data..." />
      </div>
    )
  }

  if (error || !formData || !payersData) {
    return (
      <div className="space-y-4">
        <div className="text-orange py-4">Error loading insurance data. Please try again.</div>
        <div className="flex justify-end">
          <Button variant="secondary" onClick={onComplete}>
            Cancel
          </Button>
        </div>
      </div>
    )
  }

  const payers = payersData.payers || []

  const handleSelectionChange = (payerType: string, payerId: string | null) => {
    setSaveError(null)
    setDropdownSelections({
      ...dropdownSelections,
      [payerType]: payerId,
    })
  }

  const handleSave = async () => {
    const changedPayerTypes = formData.payer_types.filter((pt) => {
      const newSelection = dropdownSelections[pt.payer_type]
      return newSelection !== undefined && newSelection !== pt.current_payer_id
    })

    try {
      setSaveError(null)
      await Promise.all(
        changedPayerTypes.map((pt) => {
          const payerId = dropdownSelections[pt.payer_type]
          if (!payerId || !pt.apero_id) {
            return Promise.reject(new Error('Missing required payer data'))
          }
          return updateMutation.mutateAsync({
            path: {
              client_id: clientId,
              payer_type: pt.payer_type,
            },
            body: {
              payer: {
                payer_id: payerId,
                apero_id: pt.apero_id,
              },
            },
          })
        })
      )

      onComplete()
    } catch (error) {
      console.error('Error saving insurance data:', error)
      setSaveError('Error saving insurance data. Please try again.')
    }
  }

  const hasChanges = formData.payer_types.some((pt) => {
    const newSelection = dropdownSelections[pt.payer_type]
    return newSelection !== undefined && newSelection !== pt.current_payer_id
  })

  const formatPayerTypeLabel = (payerType: string): string => {
    return `${payerType.charAt(0).toUpperCase()}${payerType.slice(1)} Insurance`
  }

  const isSaving = updateMutation.isPending

  return (
    <div className="space-y-6">
      {formData.payer_types.map((pt) => {
        const hasAperoId = pt.apero_id != null

        return (
          <div key={pt.payer_type} className="space-y-3">
            <h3 className="text-lg font-semibold text-charcoal-75">
              {formatPayerTypeLabel(pt.payer_type)}
            </h3>

            {hasAperoId ? (
              <PayerCombobox
                payers={payers}
                label="Select Payer"
                value={dropdownSelections[pt.payer_type] ?? pt.current_payer_id ?? null}
                onChange={(payerId) => handleSelectionChange(pt.payer_type, payerId)}
                disabled={isSaving}
                placeholder="Search for a payer..."
              />
            ) : (
              <div className="text-sm text-charcoal-50">No payer available</div>
            )}

            <div className="text-sm space-y-1">
              <div className="text-charcoal-50">
                <span className="font-medium">Current payer:</span>{' '}
                <span className="text-charcoal-75">{pt.current_payer_name || 'None'}</span>
              </div>
              <div className="text-charcoal-50">
                <span className="font-medium">Apero ID:</span>{' '}
                <span className="text-charcoal-75">{pt.apero_id ?? 'None'}</span>
              </div>
            </div>
          </div>
        )
      })}

      {saveError && (
        <div className="text-sm text-red-600 p-3 bg-red-50 rounded border border-red-200">
          {saveError}
        </div>
      )}

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-stroke">
        <Button variant="primary" onClick={handleSave} disabled={!hasChanges || isSaving}>
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  )
}
